import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Inject, Output, QueryList, ViewChild, ViewChildren, DOCUMENT, Input } from '@angular/core';
import { ChatMessage, ChatMessages, ChatReasoningStep } from 'src/core/types/chat.types';
import { AuthService } from 'src/core/services/auth.service';
import { CommonModule } from '@angular/common';
import { MarkdownPipe } from 'src/core/pipes/markdown.pipe';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { filter } from 'rxjs';
import { ChatService } from 'src/core/services/chat.service';
import { DeviceService } from 'src/core/services/device.service';
import { WidgetService } from 'src/core/services/widget.service';
import { UserData } from 'src/core/types/user.types';
import { animate, query, style, transition, trigger } from '@angular/animations';
import { ChatSearchComponent } from './chat-search/chat-search.component';
import { workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { HistoryService } from 'src/core/services/history.service';
import { ChatToggleComponent } from './chat-toggle/chat-toggle.component';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSystem } from 'src/core/types/selectedSystem.types';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';
import { BusinessPartnerAccessService } from 'src/core/services/business-partner-access.service';
import { environment } from 'src/environments/environment';
import { ModalService } from 'src/core/services/modal.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { InfoModalComponent } from '../modals/info-modal/info-modal.component';


@Component({
  selector: 'app-chatbot',
  imports: [CommonModule, MarkdownPipe, ChatSearchComponent, NgShuiModule, FormsModule, ChatToggleComponent, TranslocoModule],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  animations: [
    trigger('fadeSwitch', [
      transition('* <=> *', [
        query(':leave', [
          style({ opacity: 1 }),
          animate('200ms ease-out', style({ opacity: 0 }))
        ], { optional: true }),
        query(':enter', [
          style({ opacity: 0 }),
          animate('200ms ease-in', style({ opacity: 1 }))
        ], { optional: true })
      ])
    ])
  ]
})
export class ChatbotComponent {

  @Input() pipMode: boolean = false;
  private expandedReasoningMessages = new Set<string>();
  private tableRenderCache = new Map<string, { text: string; tableHtml: string }>();
  aiPipOpen: boolean;
  @Output() aiPipOpenChange = new EventEmitter<boolean>();
  @Output() suggestionsEnabledChange = new EventEmitter<boolean>();

  constructor(
    private authService: AuthService,
    private loadingService: LoadingService,
    protected selectedSystemService: SelectedsystemService,
    protected chatService: ChatService,
    private deviceService: DeviceService,
    @Inject(DOCUMENT) public document: Document,
    private widgetService: WidgetService,
    private http: HttpClient,
    private historyService: HistoryService,
    private selectedContentService: SelectedContentService,
    private selectedSiteService: SelectedSiteService,
    private bpAccessService: BusinessPartnerAccessService,
    private modalService: ModalService,
    private translocoService: TranslocoService
  ) {
    this.userData = authService.userData;

  }

  @ViewChild('chats', { static: false }) chats!: ElementRef;
  @ViewChild('searchbar', { static: false }) searchbar!: ElementRef;
  @ViewChild('notipopper', { static: false }) notipopper!: ElementRef;
  @ViewChildren('chatmessage') chatmessage!: QueryList<ElementRef>;
  @ViewChild('aimenu') aimenu: ElementRef;
  @ViewChild('aiicon') aiicon: ElementRef;
  @ViewChild('aiiconPip') aiiconPip: ElementRef;
  @ViewChild('versionmenu') versionmenu: ElementRef;
  @ViewChild('versionicon') versionicon: ElementRef;

  displayedMessages: ChatMessages = [];
  userData: UserData;
  isStreaming: boolean = false;
  initialViewActive: boolean = true;
  autoscroll: boolean = false;
  device;
  prompts = [];
  promptsSite = [];
  screenInfo$ = this.deviceService.screenInfo$; // expose observable
  errorChat: boolean = false;
  size: workareaSize;
  isPromptsLoading: boolean;
  isPromptsError: boolean;
  isPromptsSiteLoading: boolean;
  isPromptsSiteError: boolean;
  isFeedbackLoading: boolean = false;
  isFeedbackError: boolean = false;
  aiOpen: boolean = false;
  versionOpen: boolean = false;
  @Input() suggestionsEnabled: boolean = true;
  selectedType: 'device' | 'site' | 'none' = 'none';
  imagePreviewVisible: boolean = false;
  previewImageSrc: string = '';

  get bpCanMachineData(): boolean {
    return this.bpAccessService.canViewMachineData;
  }

  get bpCanServiceData(): boolean {
    return this.bpAccessService.canViewServiceData;
  }

  get visibleDevicePrompts(): any[] {
    if (!this.bpCanMachineData) {
      return [];
    }

    return this.prompts;
  }

  get visibleSitePrompts(): any[] {
    if (this.bpCanServiceData) {
      return this.promptsSite;
    }

    return this.promptsSite.filter((prompt) => !this.isServiceHistoryPrompt(prompt));
  }


  @HostListener('window:wheel', ['$event'])
  onMouseWheel(event: WheelEvent) {
    // If the user scrolls up, disable autoscroll
    if (this.chatService.autoscroll.value) {
      this.chatService.autoscroll.next(false);
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event) {
    this.handleAIClick(event);
    this.handleVersionClick(event);
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    if (this.imagePreviewVisible) {
      this.closeImagePreview();
    }
  }

  private handleAIClick(event: Event) {
    // Non-pip popover
    if (this.aimenu && this.aiicon && this.aiOpen) {
      const clickedInside = this.aimenu.nativeElement.contains(event.target);
      const clickedIcon = this.aiicon.nativeElement.contains(event.target);
      if (!clickedInside && !clickedIcon) {
        this.aiOpen = false;
        this.aiicon.nativeElement.active = false;
      }
    }
  }

  private handleVersionClick(event: Event) {
    if (!this.versionmenu || !this.versionicon) return;

    const clickedInside = this.versionmenu.nativeElement.contains(event.target);
    const clickedIcon = this.versionicon.nativeElement.contains(event.target);

    if (!clickedInside && !clickedIcon && this.versionOpen) {
      this.versionOpen = false;
      this.versionicon.nativeElement.active = false;
    }
  }

  private isServiceHistoryPrompt(prompt: { prompt?: string; short_prompt?: string } | null | undefined): boolean {
    const promptText = `${prompt?.prompt ?? ''} ${prompt?.short_prompt ?? ''}`.toLowerCase();
    return /service\s*history/.test(promptText);
  }





  ngOnInit(): void {

    //Get Local Storage Custom AI Settings
    if (!this.pipMode) {
      const stored = localStorage.getItem('axisAiSuggestions');
      this.suggestionsEnabled = stored ? JSON.parse(stored) : true;
    }

    //Get Selection Type
    this.selectedContentService.selection$.subscribe((selection) => {
      this.selectedType = selection.kind;
    });

    //Apply selectedSystem
    this.selectedContentService.selectedSystem$
      .pipe(filter((s): s is SelectedSystem => !!s))
      .subscribe((selectedsystem) => {
        this.device = selectedsystem;
      });

    //Fuel Prompts into the Chat Service
    this.selectedSystemService.$prompts.subscribe((prompts) => {
      this.prompts = prompts;
    });

    this.loadingService.$isPromptsLoading.subscribe((isLoading) => {
      this.isPromptsLoading = isLoading;
    });

    this.loadingService.$isPromptsError.subscribe((isError) => {
      this.isPromptsError = isError;
    });

    //Fuel Site Prompts into the Chat Service
    this.selectedSiteService.$prompts.subscribe((prompts) => {

      let inter = prompts?.map(p => {
        return { prompt: p.prompt + this.selectedContentService.currentSite.Name, short_prompt: p.short_prompt + this.selectedContentService.currentSite.Name };
      }) || [];

      this.promptsSite = inter;
    });

    this.loadingService.$isPromptsSiteLoading.subscribe((isLoading) => {
      this.isPromptsSiteLoading = isLoading;
    });

    this.loadingService.$isPromptsSiteError.subscribe((isError) => {
      this.isPromptsSiteError = isError;
    });

    //Listening to new chat arriving in array and adding it to history and to 
    this.chatService.messages.subscribe(
      {
        next: (messageArray: ChatMessages) => {
          if (messageArray.length === 0) {
            this.initialViewActive = true;
            this.displayedMessages = [];
            return;
          }
          if (messageArray && messageArray.length !== 0) {
            this.initialViewActive = false;
          }

          // Append to displayed Messages
          this.displayedMessages = messageArray;

        },
        error: (err) => {
          console.error("Error in chat messages subscription:", err);
          this.errorChat = true;
        }
      });

    //Listening to Autoscroll changes  
    this.chatService.autoscroll.subscribe({
      next: (autoscroll) => {
        this.autoscroll = autoscroll;
      },
      error: (err) => {
        console.error("Error in autoscroll subscription:", err);
      }
    });

    //Listening to Scroll Pushes and Scroll
    this.chatService.doAScroll.subscribe({
      next: (random) => {
        if (this.autoscroll) {
          this.scrollToBottom();
        }
      },
      error: (err) => {
        console.error("Error in autoscroll subscription:", err);
      }
    });

    //Listening to changes in isStreaming
    this.chatService.isStreaming.subscribe({
      next: (streaming) => {
        this.isStreaming = streaming;
        if (!streaming) {
          this.errorChat = false; // Reset error state when streaming ends
        }
      },
      error: (err) => {
        console.error("Error in isStreaming subscription:", err);
      }
    });

    //Adjusting Chat Screen SIze on Workbench expand or collapse
    this.widgetService.$workareaSize.subscribe({
      next: (size) => {
        this.size = size;
        if (size !== "small" && this.widgetService.$selectedWidget.value !== "bookmark") {
          this.initialViewActive = false;
        }
        else {
          if (this.chatService.messages.value.length === 0) {
            this.initialViewActive = true;
          }
          return;
        }
      },
      error: (err) => {
        console.error("Error fetching work area size:", err);
      }
    });

  };

  ngAfterViewChecked(): void {
    this.decorateBotImages();
  }

  private decorateBotImages(): void {
    if (!this.chatmessage) {
      return;
    }

    this.chatmessage.forEach((messageRef) => {
      const container = messageRef.nativeElement as HTMLElement;
      const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];

      images.forEach((img) => {
        if (img.dataset['maxPreviewReady'] === 'true') {
          return;
        }

        if (!img.src || img.closest('.chat-image-wrapper')) {
          return;
        }

        const wrapper = this.document.createElement('span');
        wrapper.className = 'chat-image-wrapper';

        const parent = img.parentElement;
        if (!parent) {
          return;
        }

        parent.insertBefore(wrapper, img);
        wrapper.appendChild(img);

        const div = this.document.createElement('div');
        div.className = 'chat-image-overlay';
        const icon = this.document.createElement('sh-icon');
        icon.button = true;
        icon.size = 's';
        icon.className = 'maximize-image-button';
        icon.setAttribute('aria-label', this.translocoService.translate('chatbot.image.maximizeAriaLabel'));
        icon.icon = 'fullscreen';
        div.appendChild(icon);
        wrapper.appendChild(div);

        img.dataset['maxPreviewReady'] = 'true';
      });
    });
  }

  onChatContentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const button = target.closest('.maximize-image-button');

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const wrapper = button.closest('.chat-image-wrapper');
    const image = wrapper?.querySelector('img') as HTMLImageElement | null;

    if (image?.src) {
      this.openImagePreview(image.src);
    }
  }

  openImagePreview(imageSrc: string): void {
    
    this.modalService.$modalChanged.next({ modal: "image-preview", open: true, filter: [{value: imageSrc , type: "text" }] });
    // this.previewImageSrc = imageSrc;
    // this.imagePreviewVisible = true;
  }

  closeImagePreview(): void {
    this.imagePreviewVisible = false;
    this.previewImageSrc = '';
  }


  formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const today = new Date();

    // Format time as HH:MM
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    // Check if the date is today
    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return time; // Return only time if it's today
    }

    // Format date as YYYY-MM-DD
    const formattedDate = date.toISOString().split('T')[0];

    return `${formattedDate} ${time}`;
  }

  formatThoughtDuration(durationMs?: number) {
    const safeDurationMs = Math.max(durationMs || 0, 1000);
    const seconds = Math.max(1, Math.round(safeDurationMs / 1000));

    if (seconds < 60) {
      const key = seconds === 1 ? 'chatbot.reasoning.thoughtForSeconds' : 'chatbot.reasoning.thoughtForSecondsPlural';
      return this.translocoService.translate(key, { count: seconds });
    }

    const minutes = Math.max(1, Math.round(seconds / 60));
    const key = minutes === 1 ? 'chatbot.reasoning.thoughtForMinutes' : 'chatbot.reasoning.thoughtForMinutesPlural';
    return this.translocoService.translate(key, { count: minutes });
  }

  getCurrentReasoningCategory(message: ChatMessage) {
    const lastStep = message.reasoningSteps?.[message.reasoningSteps.length - 1];
    if (lastStep?.category) {
      return lastStep.category;
    }

    if (!message.reasoningTrace?.length) {
      return '';
    }

    return message.reasoningTrace[message.reasoningTrace.length - 1];
  }

  getLiveReasoningLabel(message: ChatMessage) {
    return this.getCurrentReasoningCategory(message) || this.translocoService.translate('chatbot.reasoning.thinking');
  }

  getVisibleReasoningSteps(message: ChatMessage): ChatReasoningStep[] {
    if (message.reasoningSteps?.length) {
      return message.reasoningSteps.filter((step) => !!step.category || !!step.result?.trim());
    }

    return (message.reasoningTrace || []).map((category) => ({ category }));
  }

  getReasoningResultLines(step: ChatReasoningStep): string[] {
    if (step.resultLines?.length) {
      return step.resultLines.filter((line) => !!line?.trim());
    }

    return step.result?.trim() ? [step.result] : [];
  }

  hasVisibleReasoningDetails(message: ChatMessage) {
    return this.getVisibleReasoningSteps(message).length > 0;
  }

  toggleReasoningTrace(message: ChatMessage) {
    if (!this.hasVisibleReasoningDetails(message)) {
      return;
    }

    const key = this.getReasoningTraceKey(message);

    if (this.expandedReasoningMessages.has(key)) {
      this.expandedReasoningMessages.delete(key);
      return;
    }

    this.expandedReasoningMessages.add(key);
  }

  isReasoningTraceExpanded(message: ChatMessage) {
    return this.expandedReasoningMessages.has(this.getReasoningTraceKey(message));
  }

  private getReasoningTraceKey(message: ChatMessage) {
    return `${message.chatId}-${message.number}`;
  }

  shouldMarkPromptAsOptionAnswer(): boolean {
    const previousBotMessage = [...(this.displayedMessages ?? [])]
      .reverse()
      .find((message) => message.from === 'bot' || message.from === 'bot-like');

    return !!previousBotMessage && (
      (previousBotMessage.options?.length ?? 0) > 0 ||
      (previousBotMessage.suggestions?.length ?? 0) > 0
    );
  }

  scrollToBottom(): void {
    try {
      if (this.chats) {
        const container = this.chats.nativeElement;
        setTimeout(() => container.scrollTo({ top: container.scrollHeight }), 100);

      }
    } catch (err) {
      console.error('Scroll failed', err);
    }
  }

  sendMessage(event, type: string, value?: string, family?: string, isOptionAnswer?: boolean) {

    let prompt;

    if (value) {
      //If value is set, we are using a pre-prompt
      prompt = value;
    }
    else {
      prompt = event.target["value"];
    }

    //If we deliver the family we need to pass it along to the chat service
    if (family) {
      this.chatService.addMessage(prompt, type === "pre" ? true : false, family, isOptionAnswer);
    }
    else if (isOptionAnswer) {
      this.chatService.addMessage(prompt, type === "pre" ? true : false, undefined, isOptionAnswer);
    }
    else {
      this.chatService.addMessage(prompt, type === "pre" ? true : false);
    }

    if (event.target["value"]) {
      //Clear the input field after sending the message
      event.target["value"] = "";
    }
  }

  defineChatSize(screen, view) {
    switch (screen) {
      case "XSmall":
        if (view === "initial") { return "85%"; }
        return "100%";
      case "Small":
        if (view === "initial") { return "85%"; }
        return "100%";
      case "Medium":
        if (view === "initial") { return "85%"; }
        return "100%";
      case "Large":
        if (view === "initial") { return "70%"; }
        return "63%";
      case "XLarge":
        if (view === "initial") { return "50%"; }
        return "50%";
      default:
        return "50%";
    }
  }

  defineChatPadding(screen) {
    switch (screen) {
      case "XSmall":
        return "0px";
      case "Small":
        return "0px";
      case "Medium":
        return "48px";
      case "Large":
        return "0px";
      case "XLarge":
        return "0px";
      default:
        return "0px";
    }
  }

  interruptStreaming() {
    this.chatService.interruptStreaming();
  }

  /**
   * Handle feedback selection (like / dislike) for a final answer message.
   * Disables both buttons immediately and marks mandatory state for text input.
   */
  handleFeedback(message: ChatMessage, type: 'like' | 'dislike') {
    if (!message.final_answer) { return; }
    if (message.feedbackLocked) { return; }
    message.feedbackType = type;
    message.isMandatory = type === 'dislike';
    // Initialize empty feedback text container
    if (!message.feedbackText) { message.feedbackText = ''; }
    setTimeout(() => this.chatService.doAScroll.next(), 100);
    if(message.feedbackType === 'like') {
      this.submitFeedback({ target: { disabled: false } }, message);
    } // auto-submit like feedback without requiring additional input
  }

  /**
   * Submit feedback for a message. Shows console output, can be extended for toast notifications.
   * Submit button becomes visible only when rules (mandatory length) are satisfied in template.
   */
  submitFeedback(event, message: ChatMessage) {
    event["target"].disabled = true; // disable submit button immediately
    if (!message.feedbackType) { return; }
    message.feedbackLocked = true;
    if (message.isMandatory && (!message.feedbackText || message.feedbackText.length < 10)) { return; }
    const payload = {
      "thread_id": message.chatId, // Unique thread identifier (Required)
      "uuid": message.uuid, // unique id for each request and response (Required)
      "reaction": message.feedbackType === 'like' ? "positive" : "negative", // Reaction type: "positive" or "negative" (Required)
      "feedback": message.feedbackText,
      "version": message.version
    }

    // NOTE: Replace '/api/chat/feedback' with actual backend endpoint.
    this.http.post(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/data/feedback", payload).subscribe({
      next: () => {
        this.isFeedbackLoading = true;
        message.feedbackSubmitted = true;
        console.log('Feedback submitted successfully');

        this.historyService.addMessageArray(message).subscribe({
          next: () => {
            this.historyService.history.next();
            this.isFeedbackLoading = false;
            event["target"].disabled = false; // disable submit button immediately
          },
          error: (err) => {
            console.error('Error adding message to history:', err);
            this.isFeedbackLoading = false;
            this.isFeedbackError = true;
            event["target"].disabled = false;
          }
        });

      },
      error: (err) => {
        console.error('Feedback submit failed', err);
        event["target"].disabled = false;
      }
    });
  }

  onToggleChanged(value: boolean) {
    if (this.pipMode) {
      this.aiPipOpen = false;
      this.aiPipOpenChange.emit(false);
    } else if (this.aimenu) {
      this.aimenu.nativeElement.visible = false;
      this.aiOpen = false;
    }
    localStorage.setItem('axisAiSuggestions', JSON.stringify(value));
    this.suggestionsEnabledChange.emit(value);
  }

  openAI() {
    this.aiOpen = true;
    this.aiicon.nativeElement.active = true;
  }

  openAIPip() {
    this.aiPipOpen = true;
    this.aiPipOpenChange.emit(true);
    if (this.aiiconPip) {
      this.aiiconPip.nativeElement.active = true;
    }
  }

  aiClosed() {
    if (this.aiicon) {
      this.aiicon.nativeElement.active = false;
    }
    this.aiOpen = false;
  }

  aiPipClosed() {
    if (this.aiiconPip) {
      this.aiiconPip.nativeElement.active = false;
    }
    this.aiPipOpen = false;
    this.aiPipOpenChange.emit(false);
  }

  openVersion() {
    this.versionOpen = true;
    this.versionicon.nativeElement.active = true;
  }

  versionClosed() {
    this.versionicon.nativeElement.active = false;
    this.versionOpen = false;
  }

  getMessageTextWithoutTable(messageText: string | null | undefined): string {
    return this.parseMessageAndTable(messageText).text;
  }

  getMessageTableHtml(messageText: string | null | undefined): string {
    return this.parseMessageAndTable(messageText).tableHtml;
  }

  private parseMessageAndTable(messageText: string | null | undefined): { text: string; tableHtml: string } {
    const source = messageText || '';
    const cached = this.tableRenderCache.get(source);
    if (cached) {
      return cached;
    }

    const lines = source.split(/\r?\n/);
    const remainingLines: string[] = [];
    const tables: string[] = [];

    let i = 0;
    while (i < lines.length) {
      if (!this.isPipeRow(lines[i])) {
        remainingLines.push(lines[i]);
        i += 1;
        continue;
      }

      const blockStart = i;
      while (i < lines.length && this.isPipeRow(lines[i])) {
        i += 1;
      }

      const block = lines.slice(blockStart, i);
      const tableHtml = this.convertPipeBlockToTableHtml(block);
      if (tableHtml) {
        tables.push(tableHtml);
      } else {
        remainingLines.push(...block);
      }
    }

    const result = {
      text: remainingLines.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
      tableHtml: tables.join('')
    };

    this.tableRenderCache.set(source, result);
    return result;
  }

  private convertPipeBlockToTableHtml(block: string[]): string {
    if (!block || block.length < 2) {
      return '';
    }

    const parsedRows = block
      .map((line) => this.parsePipeRow(line))
      .filter((row) => row.length > 0);

    if (parsedRows.length < 2) {
      return '';
    }

    let headerRow = parsedRows[0];
    let dataRows = parsedRows.slice(1);

    // Support valid markdown tables with an explicit separator line.
    if (dataRows.length > 0 && this.isMarkdownSeparatorRow(block[1])) {
      dataRows = parsedRows.slice(2);
    }

    if (headerRow.length === 0 || dataRows.length === 0) {
      return '';
    }

    const columnCount = headerRow.length;
    dataRows = dataRows.map((row) => this.normalizeRowLength(row, columnCount));

    const head = `<thead><tr>${headerRow
      .map((cell) => `<th>${this.formatTableCellContent(cell)}</th>`)
      .join('')}</tr></thead>`;

    const body = `<tbody>${dataRows
      .map((row) => `<tr>${row.map((cell) => `<td>${this.formatTableCellContent(cell)}</td>`).join('')}</tr>`)
      .join('')}</tbody>`;

    return `<table class="chat-response-table">${head}${body}</table>`;
  }

  private parsePipeRow(line: string): string[] {
    let normalized = line.trim();
    if (normalized.startsWith('|')) {
      normalized = normalized.slice(1);
    }
    if (normalized.endsWith('|')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized
      .split('|')
      .map((cell) => cell.trim());
  }

  private normalizeRowLength(row: string[], columns: number): string[] {
    if (row.length === columns) {
      return row;
    }
    if (row.length > columns) {
      return row.slice(0, columns);
    }
    return [...row, ...new Array(columns - row.length).fill('')];
  }

  private isPipeRow(line: string | undefined): boolean {
    if (!line) {
      return false;
    }
    return (line.match(/\|/g) || []).length >= 2;
  }

  private isMarkdownSeparatorRow(line: string): boolean {
    const compact = (line || '').replace(/\s+/g, '');
    return /^\|?(:?-{3,}:?\|)+:?-{3,}:?\|?$/.test(compact);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private formatTableCellContent(value: string): string {
    const escaped = this.escapeHtml(value || '');
    // Allow inline markdown bold markers in table cells.
    const formatted = escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.+?)__/g, '<strong>$1</strong>')
      .replace(/\r?\n/g, '<br/>');

    return `<span class="chat-cell-content">${formatted}</span>`;
  }
}